import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const DeckUpload: React.FC = () => {
  const { token } = useAuth();
  const [deckFile, setDeckFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [message, setMessage] = useState('');

  const handleDeckFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setDeckFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!deckFile) {
      setMessage('Please select a deck file');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('file', deckFile);

      const response = await fetch('http://localhost:8000/decks/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to upload deck: ${errorText}`);
      }

      const data = await response.json();
      setResult(data);
      setMessage('Deck uploaded and analyzed successfully!');
      
    } catch (error) {
      setMessage(`Error: ${error instanceof Error ? error.message : 'Upload failed'}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Upload Deck for Pricing (Scryfall)</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Upload Deck File</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deck File (TXT, JSON, or other format)
                </label>
                <input
                  type="file"
                  accept=".txt,.json,.csv"
                  onChange={handleDeckFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {deckFile && (
                  <p className="mt-1 text-sm text-green-600">Selected: {deckFile.name}</p>
                )}
              </div>

              <button
                onClick={handleUpload}
                disabled={uploading || !deckFile}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {uploading ? 'Processing...' : 'Upload and Price Deck'}
              </button>

              {message && (
                <div className={`p-4 rounded-lg ${
                  message.includes('successfully') 
                    ? 'bg-green-50 text-green-800' 
                    : 'bg-red-50 text-red-800'
                }`}>
                  {message}
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          {result && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Deck Analysis Results</h2>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-700 mb-2">Total Deck Value</h3>
                  <p className="text-2xl font-bold text-green-600">
                    €{result.total_cost?.toFixed(2) || 'N/A'}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-700 mb-2">Budget Status</h3>
                  <p className={`text-lg font-semibold ${result.within_budget ? 'text-green-600' : 'text-red-600'}`}>
                    {result.within_budget ? 'Within Budget ✓' : 'Over Budget ✗'}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-700 mb-2">Card Count</h3>
                  <p className="text-lg text-gray-600">
                    {result.deck_breakdown?.length || 0} cards processed
                  </p>
                </div>

                {result.missing_cards && result.missing_cards.length > 0 && (
                  <div className="bg-red-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-red-700 mb-2">Missing Cards</h3>
                    <p className="text-red-600 text-sm">
                      {result.missing_cards.join(', ')}
                    </p>
                  </div>
                )}

                {result.unresolved_cards && result.unresolved_cards.length > 0 && (
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-yellow-700 mb-2">Unresolved Cards</h3>
                    <p className="text-yellow-600 text-sm">
                      {result.unresolved_cards.join(', ')}
                    </p>
                  </div>
                )}

                {result.deck_breakdown && result.deck_breakdown.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Card Breakdown</h3>
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {result.deck_breakdown.map((card: any, index: number) => (
                        <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span className="text-sm">{card.card_name} (x{card.quantity})</span>
                          <span className="text-sm font-semibold">€{card.price_eur?.toFixed(2) || 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 bg-blue-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-800 mb-2">Scryfall Data Source</h3>
          <p className="text-blue-700 mb-2">
            This system uses Scryfall's comprehensive Magic: The Gathering database for pricing and card information.
          </p>
          <ul className="text-blue-700 space-y-1">
            <li>• <strong>Data Source:</strong> Scryfall Bulk Data API</li>
            <li>• <strong>Pricing:</strong> EUR, USD, and TIX pricing available</li>
            <li>• <strong>Coverage:</strong> All Magic cards with legality and pricing data</li>
            <li>• <strong>Format Support:</strong> Commander, Modern, Legacy, Vintage, and more</li>
          </ul>
        </div>

        <div className="mt-4 bg-yellow-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">Supported Deck Formats</h3>
          <ul className="text-yellow-700 space-y-1">
            <li>• Plain text files with card names (one per line)</li>
            <li>• JSON files with deck structure</li>
            <li>• CSV files with card data</li>
            <li>• The system will match cards against Scryfall's database</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DeckUpload;
